import {
  Manager,
  ManagerPage,
  ManagerPageQuery,
  ManagerRequest,
  ManagerUpdate,
} from "@/types/manager.definition";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import managerApi from "../../services/manager.api";
import {
  createManagerFailure,
  createManagerRequest,
  createManagerSuccess,
  fetchManagerByIdFailure,
  fetchManagerByIdRequest,
  fetchManagerByIdSuccess,
  fetchManagersFailure,
  fetchManagersPageFailure,
  fetchManagersPageRequest,
  fetchManagersPageSuccess,
  fetchManagersRequest,
  fetchManagersSuccess,
  reloadManagersPage,
  selectManagersPageQuery,
  updateManagerFailure,
  updateManagerRequest,
  updateManagerSuccess,
} from "../reducers/managersSlice";

// Sagas
function* fetchManagersSaga() {
  try {
    const managers: Manager[] = yield call(managerApi.getAllManagers);
    yield put(fetchManagersSuccess(managers));
  } catch (error) {
    yield put(
      fetchManagersFailure(
        error instanceof Error ? error.message : "Failed to fetch managers"
      )
    );
  }
}

// MED-1635: one server-side page of the managers list
function* fetchManagersPageSaga(action: { payload: ManagerPageQuery }) {
  try {
    const page: ManagerPage = yield call(
      managerApi.getManagersPage,
      action.payload
    );
    yield put(fetchManagersPageSuccess(page));
  } catch (error) {
    yield put(
      fetchManagersPageFailure(
        error instanceof Error ? error.message : "Failed to fetch managers"
      )
    );
  }
}

/** Reloads the page currently shown; nothing to do if the list was never loaded. */
function* reloadManagersPageSaga() {
  const query: ManagerPageQuery | null = yield select(selectManagersPageQuery);
  if (query) {
    yield put(fetchManagersPageRequest(query));
  }
}

function* fetchManagerByIdSaga(action: { payload: string }) {
  try {
    const manager: Manager = yield call(
      managerApi.getManagerById,
      action.payload
    );
    yield put(fetchManagerByIdSuccess(manager));
  } catch (error) {
    yield put(
      fetchManagerByIdFailure(
        error instanceof Error ? error.message : "Failed to fetch manager"
      )
    );
  }
}

function* createManagerSaga(action: { payload: ManagerRequest }) {
  try {
    const newManager: Manager = yield call(
      managerApi.createManager,
      action.payload
    );
    yield put(createManagerSuccess(newManager));
  } catch (error) {
    yield put(
      createManagerFailure(
        error instanceof Error ? error.message : "Failed to create manager"
      )
    );
  }
}

function* updateManagerSaga(action: {
  payload: { managerId: string; data: ManagerUpdate };
}) {
  try {
    const { managerId, data } = action.payload;
    const updatedManager: Manager = yield call(
      managerApi.updateManager,
      managerId,
      data
    );
    yield put(updateManagerSuccess(updatedManager));
  } catch (error) {
    yield put(
      updateManagerFailure(
        error instanceof Error ? error.message : "Failed to update manager"
      )
    );
  }
}

// Root saga
export function* managerSaga() {
  yield takeLatest(fetchManagersRequest, fetchManagersSaga);
  yield takeLatest(fetchManagersPageRequest, fetchManagersPageSaga);
  yield takeEvery(reloadManagersPage, reloadManagersPageSaga);
  yield takeLatest(fetchManagerByIdRequest, fetchManagerByIdSaga);
  yield takeEvery(createManagerRequest, createManagerSaga);
  yield takeEvery(updateManagerRequest, updateManagerSaga);
}
